import { useEffect, useMemo, useState } from "react";
import { statusLabel } from "@/lib/saleStatus";
import { useIsDesktop } from "@/lib/useMediaQuery";
import {
  listCustomers, bulkUploadCustomers, listAccountability, memberManagerReports,
  memberManagerCoachStats, restoreCustomer, listRoster, upsertCustomerMeta,
  bulkAssignMemberManager,
  listRenewals,
  getMemberSummary,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast, canWrite } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import {
  Search, Users2, Repeat, TrendingUp, AlertCircle, UploadCloud, FileSpreadsheet, CheckCircle2,
  BellRing, DoorOpen, ChevronUp, ChevronDown, UserCog, Undo2, CheckSquare, Square,
} from "lucide-react";
import RenewalNudgePanel, { ExitDialog } from "@/components/RenewalNudgePanel";
import { logRenewal } from "@/lib/renewal";
import MemberManagerPanel from "@/components/MemberManagerPanel";
import { numberLocale } from "@/lib/money";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);

// A customer is "expired" if their most-recent programme_end_date is strictly before today.
// "Active" means we have an end_date and it's >= today. Anyone without an end_date falls under "All".
function lifecycleOf(c) {
  // Backend decides (programme started and not expired = active, paid but not started,
  // expired, or no expiry on file); the date maths below is a fallback for old payloads.
  if (c.lifecycle) return c.lifecycle;
  if (!c.programme_end_date) return "unknown";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(c.programme_end_date); end.setHours(0, 0, 0, 0);
  return end < today ? "expired" : "active";
}

export default function Customers() {
  const { user } = useAuth();
  const canUpload = canWrite(user) && atLeast(user?.role, "manager");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const isDesktop = useIsDesktop();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [tab, setTab] = useState("active");                // active | renewals | exited
  const [lifecycle, setLifecycle] = useState("all");        // all | active | expired (chips inside "active" tab)
  const [coachFilter, setCoachFilter] = useState("all");    // all | <coach name>
  const [feKeys, setFeKeys] = useState(new Set());          // customer keys currently in an active FE programme
  const [mmReports, setMmReports] = useState({ stale_updates: [], expired_no_reason: [] });
  const [coachStats, setCoachStats] = useState([]);
  const [showCoachDash, setShowCoachDash] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [bulk, setBulk] = useState(new Set()); // customer_keys selected for bulk actions
  const [renewals, setRenewals] = useState(null);       // Up for renewal, default view (CR-0076/77)
  const [exiting, setExiting] = useState(null); // member being exited from the detail panel
  const refresh = () => {
    listRenewals().then(setRenewals).catch(() => setRenewals({ window_days: 30, items: [] }));
    listCustomers().then((newRows) => {
      setRows(newRows);
      // Re-sync the currently-selected customer so newly-persisted fields (e.g. member_manager)
      // reflect immediately in the detail pane without a full page reload.
      setSelected((prev) => {
        if (!prev) return prev;
        const updated = (newRows || []).find((c) => c.key === prev.key);
        return updated || prev;
      });
    });
    memberManagerCoachStats().then(setCoachStats).catch(() => setCoachStats([]));
    memberManagerReports().then(setMmReports).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    // Load accountability once to figure out which customers are currently in the FE programme.
    listAccountability().then((rows) => {
      const keys = new Set();
      for (const r of rows || []) {
        if ((r.programme_status || "").toLowerCase() === "active" && !(r.converted_program || "")) {
          if (r.phone) keys.add(r.phone);
          if (r.client_name) keys.add(r.client_name.trim().toLowerCase());
        }
      }
      setFeKeys(keys);
    }).catch(() => setFeKeys(new Set()));
    // Coach dropdown = every member_manager on the Team Roster
    listRoster("member_manager").then(setCoaches).catch(() => setCoaches([]));
  }, []);
  const isFE = (c) => feKeys.has(c.phone) || feKeys.has((c.name || "").trim().toLowerCase());
  const needsReasonSet = new Set((mmReports.expired_no_reason || []).map((r) => r.customer_key));
  const staleSet = new Set((mmReports.stale_updates || []).map((r) => r.customer_key));

  // Coach filter helper — matches on customer's most-recent member_manager (from list_customers).
  const matchesCoach = (c) => coachFilter === "all" || (c.member_manager || "").trim() === coachFilter;

  const activeRows = rows.filter((c) => !c.exited);
  const exitedRows = rows.filter((c) => c.exited);
  const upForRenewalCount = renewals ? renewals.items.length : 0;
  const inProgrammeCount = (activeRows || []).filter((c) => lifecycleOf(c) === "active").length;
  const programmeEndedCount = (activeRows || []).filter((c) => lifecycleOf(c) === "expired").length;
  const expiryUnknownCount = (activeRows || []).filter((c) => lifecycleOf(c) === "unknown").length;
  const notStartedCount = (activeRows || []).filter((c) => lifecycleOf(c) === "not_started").length;

  // Rows visible in the currently-selected tab (with search + coach filter applied)
  let visible = [];
  if (tab === "active") {
    visible = (activeRows || []).filter((c) => lifecycle === "all" || lifecycleOf(c) === lifecycle);
  } else if (tab === "exited") {
    visible = exitedRows;
  }
  const filtered = (visible || []).filter((c) => {
    if (!matchesCoach(c)) return false;
    return !q || [c.name, c.phone, c.member_manager].some((v) => (v || "").toString().toLowerCase().includes(q.toLowerCase()));
  });
  const repeaters = (activeRows || []).filter((c) => c.visits > 1).length;
  const totalRevenue = rows.reduce((a, c) => a + (c.total_net || 0), 0);

  useEffect(() => { setBulk(new Set()); }, [tab, coachFilter, lifecycle, q]);
  const toggleBulk = (key) => {
    setBulk((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const unassignedInView = (filtered || []).filter((c) => !(c.member_manager || "").trim());
  const selectAllUnassigned = () => setBulk(new Set((unassignedInView || []).map((c) => c.key)));
  const clearBulk = () => setBulk(new Set());

  const memberDetail = selected && (
            <div data-testid="customer-detail">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="eyebrow">Member</div>
                  <h3 className="font-display text-2xl font-extrabold tracking-tight mt-1">{selected.name}</h3>
                  <div className="text-xs text-zinc-500 mt-1">{selected.phone || "no phone"} · {selected.visits} sales · first {new Date(selected.first_seen).toLocaleDateString()} · last {new Date(selected.last_seen).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Lifetime net</div>
                  <div className="font-display text-3xl font-extrabold text-emerald-400 tabular-nums mt-0.5">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(selected.total_net)}</div>
                  {selected.total_pending > 0 && <div className="text-xs text-amber-400 mt-1">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(selected.total_pending)} pending</div>}
                </div>
              </div>

              <MembershipSummary memberKey={selected.key} />

              <div className="mt-5 flex flex-wrap gap-2 text-[11px]">
                {(selected.programmes || []).map((p) => <span key={p} className="px-2.5 py-1 rounded-full border border-[#262626] bg-[#0d0d0d]">{p}</span>)}
                {(selected.fe_pt_pe_types || []).map((t) => <span key={t} className="px-2.5 py-1 rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-300 font-mono">{t}</span>)}
              </div>

              {selected.renewal_due && (
                <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="detail-renewal-actions">
                  <span className="text-[11px] text-rose-300">Renewal due</span>
                  <button
                    onClick={() => logRenewal({ name: selected.name, phone: selected.phone, programme: selected.programme_end_plan || (selected.sales || []).find((s) => s.programme)?.programme })}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                    data-testid="detail-log-renewal-btn"
                  >
                    Log renewal
                  </button>
                  <button
                    onClick={() => setExiting(selected)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border border-rose-500/50 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition-colors"
                    data-testid="detail-log-exit-btn"
                  >
                    <DoorOpen className="h-3 w-3" /> Log exit
                  </button>
                </div>
              )}

              <MemberManagerAssignBar
                selected={selected}
                coaches={coaches}
                onAssigned={() => refresh()}
              />

              <div className="eyebrow mt-6 mb-3">Sales history</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Programme</th>
                      <th className="px-3 py-2">Mode</th>
                      <th className="px-3 py-2 text-right">Paid</th>
                      <th className="px-3 py-2 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.sales || []).map((s, i) => (
                      <tr key={s.id || `${s.date}-${i}`} className="border-b border-[#1a1a1a] last:border-0">
                        <td className="px-3 py-2 text-zinc-400 text-xs whitespace-nowrap">{new Date(s.date).toLocaleDateString()}</td>
                        <td className="px-3 py-2 text-xs font-mono">{s.fe_pt_pe || "—"}</td>
                        <td className="px-3 py-2 text-xs text-zinc-400">{statusLabel(s.status, s) || "—"}</td>
                        <td className="px-3 py-2 text-xs text-zinc-400">{s.programme || "—"}</td>
                        <td className="px-3 py-2 text-xs text-zinc-400">{s.payment_mode || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(s.paid)}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(s.net_received)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6">
                <MemberManagerPanel
                  customer={selected}
                  lifecycle={lifecycleOf(selected)}
                  isFE={isFE(selected)}
                />
              </div>
            </div>
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Existing members</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Members</h2>
          <p className="text-sm text-zinc-400 mt-1">Everyone we have ever taken money from — grouped by phone (or name if no phone).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {canUpload && (
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger data-testid="customers-dialog-trigger-133" asChild>
                <Button variant="outline" className="border-[#262626] bg-transparent hover:bg-[#141414] rounded-full max-md:hidden" data-testid="bulk-upload-btn">
                  <UploadCloud className="h-4 w-4 mr-1.5" /> Bulk upload CSV
                </Button>
              </DialogTrigger>
              <BulkUploadDialog onDone={() => { setUploadOpen(false); refresh(); }} />
            </Dialog>
          )}
          <div className="relative w-full sm:w-auto">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone…" className="pl-9 bg-[#0a0a0a] border-[#262626] w-full sm:w-56" data-testid="customers-search" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Kpi label="Active members" value={inProgrammeCount} icon={Users2} />
        <Kpi label="Repeat members" value={repeaters} icon={Repeat} tint="emerald" />
        <button
          onClick={() => setTab("renewals")}
          data-testid="renewal-due-filter"
          className={`card-surface p-5 text-left transition-colors ${tab === "renewals" ? "border-rose-500/50 bg-rose-500/5" : "hover:border-rose-500/30"}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Up for renewal</div>
            <AlertCircle className={`h-4 w-4 ${upForRenewalCount > 0 ? "text-rose-400" : "text-zinc-500"}`} />
          </div>
          <div className={`font-display text-2xl md:text-3xl font-extrabold mt-2 tabular-nums ${upForRenewalCount > 0 ? "text-rose-400" : ""}`}>{upForRenewalCount}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Tap to open Up for renewal tab</div>
        </button>
        <Kpi label="Lifetime revenue" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(totalRevenue)}`} icon={TrendingUp} tint="amber" />
      </div>

      {/* Coach filter dropdown — applies to all tabs */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="coach-filter-row">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <UserCog className="h-3.5 w-3.5" />
          <span className="uppercase tracking-[0.18em] text-[10px]">Filter by Member Manager</span>
        </div>
        <Select data-testid="customers-select-172" value={coachFilter} onValueChange={setCoachFilter}>
          <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-full sm:w-64" data-testid="coach-filter"><SelectValue placeholder="All coaches" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
            <SelectItem data-testid="customers-select-item-175" value="all">All coaches</SelectItem>
            {(() => {
              // Merge roster coaches with any coach seen in coachStats so filtering-from-a-card
              // always shows the correct label in the trigger.
              const merged = new Map();
              (coaches || []).forEach((c) => merged.set(c.name, c.name));
              (coachStats || []).forEach((s) => { if (s.coach) merged.set(s.coach, s.coach); });
              return Array.from(merged.values()).sort().map((name) => (<SelectItem data-testid="customers-select-item-182" key={name} value={name}>{name}</SelectItem>));
            })()}
          </SelectContent>
        </Select>
        {coachFilter !== "all" && (
          <button onClick={() => setCoachFilter("all")} className="text-[11px] text-zinc-400 hover:text-zinc-200 underline" data-testid="coach-filter-clear">
            clear
          </button>
        )}
        {tab !== "renewals" && (
          <div className="text-[11px] text-zinc-500">
            <span className="text-zinc-200 font-medium tabular-nums">{filtered.length}</span> {tab === "exited" ? "exited" : "members"} shown
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-[#1f1f1f] flex items-center gap-6 overflow-x-auto whitespace-nowrap no-scrollbar" data-testid="customers-tabs">
        <button
          onClick={() => setTab("active")}
          className={`pb-3 -mb-px text-sm font-medium transition-colors ${tab === "active" ? "text-zinc-100 border-b-2 border-blue-500" : "text-zinc-500 hover:text-zinc-300"}`}
          data-testid="tab-active"
        >
          <Users2 className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" /> Members <span className="text-[10px] text-zinc-500 tabular-nums">({activeRows.length})</span>
        </button>
        <button
          onClick={() => setTab("renewals")}
          className={`pb-3 -mb-px text-sm font-medium transition-colors ${tab === "renewals" ? "text-zinc-100 border-b-2 border-rose-500" : "text-zinc-500 hover:text-zinc-300"}`}
          data-testid="tab-renewals"
        >
          <BellRing className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" /> Up for renewal <span className={`text-[10px] tabular-nums ${upForRenewalCount > 0 ? "text-rose-400" : "text-zinc-500"}`}>({upForRenewalCount})</span>
        </button>
        <button
          onClick={() => setTab("exited")}
          className={`pb-3 -mb-px text-sm font-medium transition-colors ${tab === "exited" ? "text-zinc-100 border-b-2 border-zinc-400" : "text-zinc-500 hover:text-zinc-300"}`}
          data-testid="tab-exited"
        >
          <DoorOpen className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" /> Exited <span className="text-[10px] text-zinc-500 tabular-nums">({exitedRows.length})</span>
        </button>
      </div>

      {tab === "renewals" && (
        <RenewalNudgePanel initial={renewals} q={q} coach={coachFilter} onCustomerExited={() => refresh()} />
      )}

      {tab === "exited" && <ExitedTable rows={filtered} onRestore={(key) => restoreCustomer(key).then(() => { toast.success("Restored to Active"); refresh(); }).catch(() => toast.error("Restore failed"))} canManage={atLeast(user?.role, "manager")} />}

      {tab === "active" && (
        <>
          {/* In-programme / Programme-ended chips */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="lifecycle-filter">
            {[
              { key: "all", label: `All members (${activeRows.length})` },
              { key: "active", label: `Active (${inProgrammeCount})`, tint: "emerald" },
              ...(notStartedCount ? [{ key: "not_started", label: `Not started (${notStartedCount})`, tint: "amber" }] : []),
              { key: "expired", label: `Expired (${programmeEndedCount})`, tint: "rose" },
              ...(expiryUnknownCount ? [{ key: "unknown", label: `Expiry unknown (${expiryUnknownCount})`, tint: "amber" }] : []),
            ].map((chip) => {
              const on = lifecycle === chip.key;
              const tintCls = chip.tint === "emerald"
                ? (on ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-[#262626] hover:border-emerald-500/30 text-zinc-400")
                : chip.tint === "rose"
                  ? (on ? "border-rose-500/50 bg-rose-500/10 text-rose-300" : "border-[#262626] hover:border-rose-500/30 text-zinc-400")
                : chip.tint === "amber"
                  ? (on ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-[#262626] hover:border-amber-500/30 text-zinc-400")
                  : (on ? "border-blue-500/50 bg-blue-500/10 text-blue-300" : "border-[#262626] hover:border-blue-500/30 text-zinc-400");
              return (
                <button
                  key={chip.key}
                  onClick={() => setLifecycle(chip.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${tintCls}`}
                  data-testid={`chip-${chip.key}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <BulkAssignBar
            selected={bulk}
            unassignedInView={unassignedInView.length}
            coaches={coaches}
            onSelectAll={selectAllUnassigned}
            onClear={clearBulk}
            onAssigned={() => { clearBulk(); refresh(); }}
          />
          <div className="card-surface overflow-hidden">
            <div className="lg:max-h-[70vh] lg:overflow-y-auto">
              {(filtered || []).map((c) => {
                const checked = bulk.has(c.key);
                return (
                <div
                  key={c.key}
                  data-testid={`customer-${c.key}`}
                  className={`w-full px-4 lg:px-5 py-4 border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors flex items-start gap-3 ${selected?.key === c.key ? "bg-[#151a20] border-l-2 border-l-blue-500" : ""}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleBulk(c.key); }}
                    className={`shrink-0 mt-1 h-5 w-5 lg:h-4 lg:w-4 rounded border grid place-items-center transition-colors ${checked ? "border-blue-500 bg-blue-500/20 text-blue-300" : "border-[#333] hover:border-blue-500/60"}`}
                    data-testid={`bulk-check-${c.key}`}
                    aria-label={checked ? "Deselect" : "Select"}
                  >
                    {checked ? <CheckSquare className="h-3 w-3" /> : null}
                  </button>
                  <button
                    onClick={() => setSelected(c)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`customer-open-${c.key}`}
                  >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate">{c.name}</div>
                        {c.renewal_due && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-full px-2 py-0.5 shrink-0" data-testid={`renewal-badge-${c.key}`}>
                            <AlertCircle className="h-2.5 w-2.5" /> Renewal due
                          </span>
                        )}
                        {needsReasonSet.has(c.key) && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-full px-2 py-0.5 shrink-0" data-testid={`needs-reason-badge-${c.key}`}>Needs reason</span>
                        )}
                        {staleSet.has(c.key) && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5 shrink-0" data-testid={`stale-badge-${c.key}`}>Stale</span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {c.phone || "—"} · {c.visits} visit{c.visits > 1 ? "s" : ""}
                        {c.member_manager && <> · <span className="text-zinc-400">MM: {c.member_manager}</span></>}
                        {c.programme_end_date && (
                          <> · ends {new Date(c.programme_end_date).toLocaleDateString()}
                            {c.days_since_end > 0 && <span className="text-rose-400 ml-1">({c.days_since_end}d ago)</span>}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(c.total_net)}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{statusLabel(c.last_status, (c.sales || [])[0] || {}) || "—"}</div>
                    </div>
                  </div>
                </button>
                </div>
                );
              })}
              {filtered.length === 0 && <div className="px-5 py-14 text-center text-sm text-zinc-500">No members yet.</div>}
            </div>
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-7">
          {selected && isDesktop ? (
            <div className="card-surface p-6">{memberDetail}</div>
          ) : (
            <div className="card-surface p-16 text-center text-sm text-zinc-500">Select a member to see their full history.</div>
          )}
        </div>
      </div>
      {/* Phones and tablets: the member opens as a full-screen sheet */}
      <Dialog open={!!selected && !isDesktop} onOpenChange={(v) => !v && setSelected(null)}>
        {selected && !isDesktop && (
          <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-sheet">
            <DialogTitle className="sr-only">{selected.name}</DialogTitle>
            {memberDetail}
          </DialogContent>
        )}
      </Dialog>
      {exiting && (
        <ExitDialog
          row={exiting}
          onClose={() => setExiting(null)}
          onSaved={() => { setExiting(null); setSelected(null); refresh(); }}
        />
      )}
        </>
      )}
    </div>
  );
}

// Membership at a glance (routes/member_summary.py): the running plan, pause days, reward points and app XP.
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS_STYLE = {
  active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  paused: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  "not started": "border-blue-500/40 bg-blue-500/10 text-blue-300",
  ended: "border-zinc-600 bg-zinc-800/40 text-zinc-400",
};

function SummaryCell({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function MembershipSummary({ memberKey }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!memberKey) return undefined;
    let live = true;
    setData(null); setFailed(false);
    getMemberSummary(memberKey).then((d) => { if (live) setData(d); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [memberKey]);
  if (failed) return <div className="mt-4 text-xs text-zinc-500">Couldn&apos;t load the membership details.</div>;
  if (!data) return <div className="mt-4 text-xs text-zinc-500" data-testid="membership-summary-loading">Loading membership…</div>;
  const m = data.membership;
  const p = data.pauses || {};
  const cur = window.__ACTIVE_CURRENCY_SYMBOL__ || "";
  const open = p.open;
  return (
    <div className="mt-5" data-testid="membership-summary">
      <div className="flex items-center gap-2 mb-2">
        <div className="eyebrow">Membership</div>
        {m && <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${STATUS_STYLE[m.status] || STATUS_STYLE.ended}`}>{m.status}</span>}
        {m?.source === "YoActiv" && <span className="text-[10px] text-zinc-500">sold in YoActiv</span>}
      </div>
      {!m ? (
        <div className="text-xs text-zinc-500">No membership on record at this gym.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <SummaryCell label="Plan" value={m.programme || "—"} sub={m.member_manager ? `Manager: ${m.member_manager}` : m.accountability_coach ? `Coach: ${m.accountability_coach}` : null} testid="ms-plan" />
          <SummaryCell label="Start" value={fmtDay(m.start_date)} testid="ms-start" />
          <SummaryCell label="End" value={fmtDay(m.end_date)}
            sub={m.days_left != null ? `${m.days_left} day${m.days_left === 1 ? "" : "s"} left` : m.original_end_date ? `was ${fmtDay(m.original_end_date)}` : null} testid="ms-end" />
          <SummaryCell label="Pauses"
            value={p.pausable ? `${p.remaining} of ${p.total} days left` : "Not pausable"}
            sub={open ? `${open.status === "active" ? "Paused" : "Pause booked"} ${fmtDay(open.start_date)} to ${fmtDay(open.end_date)}` : p.used ? `${p.used} day${p.used === 1 ? "" : "s"} used` : null}
            testid="ms-pauses" />
          <SummaryCell label="Reward points" value={`${fmtINR(data.points?.balance)} pts`}
            sub={data.points?.balance ? `worth ${cur}${fmtINR(data.points.worth)}${data.points.expires_on ? ` · expire ${fmtDay(data.points.expires_on)}` : ""}` : null}
            testid="ms-points" />
          <SummaryCell label="App XP" value={data.app ? `${fmtINR(data.app.xp)} XP` : "Not on the app"}
            sub={data.app ? `${data.app.level}${data.app.joined ? ` · joined ${fmtDay(data.app.joined)}` : ""}` : null} testid="ms-xp" />
          {(m.pending || 0) > 0 && <SummaryCell label="Balance due" value={`${cur}${fmtINR(m.pending)}`} sub={`paid ${cur}${fmtINR(m.paid)}`} testid="ms-balance" />}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint }) {
  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <Icon className={`h-4 w-4 ${tint === "emerald" ? "text-emerald-400" : tint === "amber" ? "text-amber-400" : "text-blue-400"}`} />
      </div>
      <div className={`font-display text-2xl md:text-3xl font-extrabold mt-2 tabular-nums ${tint === "emerald" ? "text-emerald-400" : tint === "amber" ? "text-amber-400" : ""}`}>{value}</div>
    </div>
  );
}


function BulkUploadDialog({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a .csv file first");
    setBusy(true);
    try {
      const r = await bulkUploadCustomers(file);
      setResult(r);
      toast.success(`${r.created} created · ${r.updated} updated${r.errors ? ` · ${r.errors} errors` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="bulk-upload-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Bulk-upload members</DialogTitle>
      </DialogHeader>
      <form data-testid="customers-form-442" onSubmit={submit} className="space-y-4 mt-2">
        <div className="text-xs text-zinc-400 leading-relaxed">
          Upload a CSV with the following columns (case-insensitive):
          <code className="block bg-[#0a0a0a] border border-[#262626] rounded px-2 py-1.5 mt-2 text-[11px] font-mono text-blue-300">
            first_name, last_name, email, phone, package, start_date, end_date
          </code>
          Phone is the natural key — existing customers with the same phone will be updated in place.
        </div>
        <label
          htmlFor="customer-csv"
          className="flex flex-col items-center justify-center border border-dashed border-[#333] rounded-xl px-6 py-8 cursor-pointer hover:border-blue-500/60 hover:bg-[#0d0d0d] transition-colors"
        >
          {file ? (
            <>
              <FileSpreadsheet className="h-6 w-6 text-emerald-400 mb-2" />
              <div className="text-sm text-emerald-300">{file.name}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
            </>
          ) : (
            <>
              <UploadCloud className="h-6 w-6 text-zinc-500 mb-2" />
              <div className="text-sm text-zinc-300">Choose a .csv file</div>
              <div className="text-[11px] text-zinc-500 mt-1">Header row required</div>
            </>
          )}
          <input
            id="customer-csv" type="file" accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            data-testid="bulk-upload-file-input"
          />
        </label>

        {result && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-3 text-xs" data-testid="bulk-upload-result">
            <div className="flex items-center gap-2 text-emerald-300 font-semibold">
              <CheckCircle2 className="h-4 w-4" /> Import complete
            </div>
            <div className="text-zinc-400 mt-1.5">
              Created <span className="text-emerald-300 font-mono">{result.created}</span> ·
              Updated <span className="text-blue-300 font-mono">{result.updated}</span>
              {result.errors > 0 && <> · Errors <span className="text-rose-300 font-mono">{result.errors}</span></>}
            </div>
            {result.error_details?.length > 0 && (
              <ul className="mt-2 text-[10px] text-rose-300 list-disc list-inside space-y-0.5">
                {(result.error_details || []).map((e) => (
                  <li key={`cust-err-${e.row}-${e.error}`}>Row {e.row}: {e.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="submit" disabled={busy || !file} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="bulk-upload-submit-btn">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function MemberManagerDashboard({ stats, show, onToggle, selected, onSelect }) {
  const list = stats || [];
  if (list.length === 0) return null;
  return (
    <section data-testid="mm-coach-dashboard">
      <button data-testid="customers-button-510" onClick={onToggle} className="w-full flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserCog className="h-3.5 w-3.5 text-blue-400" />
          <div className="eyebrow">Member Manager dashboard</div>
          <span className="text-[10px] text-zinc-500">{list.length} coach{list.length > 1 ? "es" : ""}</span>
        </div>
        {show ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>
      {show && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {list.map((s) => {
            const isSelected = selected === s.coach;
            const rate = s.follow_up_rate_pct || 0;
            const rateColor = rate >= 80 ? "text-emerald-300" : rate >= 50 ? "text-amber-300" : "text-rose-300";
            return (
              <button
                key={s.coach}
                onClick={() => onSelect(isSelected ? "all" : s.coach)}
                className={`card-surface p-4 text-left transition-all ${isSelected ? "border-blue-500/50 bg-blue-500/[0.03]" : "hover:border-blue-500/20"}`}
                data-testid={`mm-coach-card-${s.coach}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-base font-bold text-zinc-100">{s.coach}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5"><span className="text-zinc-300 font-medium">{s.total_assigned}</span> assigned</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold tabular-nums ${rateColor}`}>{rate}%</div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">follow-up</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Active</div>
                    <div className="text-zinc-200 font-medium tabular-nums">{s.active}</div>
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">In FE</div>
                    <div className="text-blue-300 font-medium tabular-nums">{s.in_fe}</div>
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Renewal</div>
                    <div className="text-amber-300 font-medium tabular-nums">{s.up_for_renewal}</div>
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Fresh &lt;14d</div>
                    <div className="text-emerald-300 font-medium tabular-nums">{s.fresh_updates}</div>
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Stale</div>
                    <div className="text-amber-300 font-medium tabular-nums">{s.stale_updates}</div>
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">No updates</div>
                    <div className="text-rose-300 font-medium tabular-nums">{s.no_updates}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
                  <span>Exited {s.exited} · Expired {s.expired}</span>
                  <span>{s.audits_recorded} audits</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ExitedTable({ rows, onRestore, canManage }) {
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  return (
    <div className="card-surface overflow-hidden" data-testid="exited-table">
      {rows.length === 0 ? (
        <div className="px-6 py-14 text-center text-sm text-zinc-500">No exited members match this filter.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 bg-[#0d0d0d] border-b border-[#1f1f1f]">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Coach</th>
                <th className="px-3 py-2 font-medium">Exited on</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium text-right">Lifetime</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((c) => (
                <tr key={c.key} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`exited-row-${c.key}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-zinc-100">{c.name}</div>
                    <div className="text-[10px] text-zinc-500">{c.phone || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-zinc-300 text-xs">{c.member_manager || "—"}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(c.exited_at)}</td>
                  <td className="px-3 py-3 text-zinc-300 text-xs max-w-md">{c.exit_reason || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-zinc-300 text-xs">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(c.total_net || 0)}</td>
                  <td className="px-3 py-3 text-right">
                    {canManage && (
                      <button
                        onClick={() => onRestore(c.key)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                        data-testid={`restore-btn-${c.key}`}
                      >
                        <Undo2 className="h-3 w-3" /> Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberManagerAssignBar({ selected, coaches, onAssigned }) {
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const mm = (selected.member_manager || "").trim();

  const assign = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await upsertCustomerMeta(selected.key, {
        member_manager: picked,
        customer_name: selected.name,
      });
      toast.success(`${picked} assigned as Member Manager`);
      setPicking(false);
      setPicked("");
      onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`mt-4 rounded-xl border px-4 py-3 flex items-center justify-between flex-wrap gap-3 ${mm ? "border-blue-500/20 bg-blue-500/[0.03]" : "border-amber-500/40 bg-amber-500/[0.03]"}`} data-testid="member-manager-bar">
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-medium ${mm ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
          {mm ? mm.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "?"}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Member Manager</div>
          {mm ? (
            <div className="text-base font-semibold text-zinc-100" data-testid="mm-assigned-name">{mm}</div>
          ) : (
            <div className="text-base font-semibold text-amber-300" data-testid="mm-not-assigned">Not yet assigned</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!picking ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPicking(true)}
            className={`border-[#262626] bg-transparent hover:bg-[#141414] rounded-full text-xs ${mm ? "" : "border-amber-500/50 text-amber-200 hover:bg-amber-500/10"}`}
            data-testid="mm-assign-toggle"
          >
            {mm ? "Change" : "Assign coach"}
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-56">
              <SearchableSelect data-testid="customers-searchable-select-683"
                value={picked}
                onChange={setPicked}
                options={coaches.length === 0
                  ? [{ value: "_none", label: "No coaches with the member_manager skill", disabled: true }]
                  : (coaches || []).map((c) => ({ value: c.name, label: c.name }))}
                placeholder="Pick a coach"
                emptyLabel="— clear —"
                testid="mm-assign-select"
              />
            </div>
            <Button size="sm" disabled={busy || !picked || picked === "_none"} onClick={assign} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="mm-assign-confirm">
              {busy ? "…" : "Assign"}
            </Button>
            <Button data-testid="customers-button-697" size="sm" variant="ghost" onClick={() => { setPicking(false); setPicked(""); }} className="text-zinc-400 hover:text-zinc-200">Cancel</Button>
          </div>
        )}
      </div>
    </div>
  );
}


function BulkAssignBar({ selected, unassignedInView, coaches, onSelectAll, onClear, onAssigned }) {
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const count = selected.size;
  const assign = async () => {
    if (!picked) return toast.error("Pick a Member Manager");
    if (count === 0) return toast.error("Select at least one member");
    setBusy(true);
    try {
      const r = await bulkAssignMemberManager(Array.from(selected), picked);
      toast.success(`${picked} assigned to ${r.updated} member${r.updated === 1 ? "" : "s"}`);
      setPicked("");
      onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk assign failed");
    } finally { setBusy(false); }
  };
  if (count === 0 && unassignedInView === 0) return null;
  return (
    <div className="mb-3 rounded-xl border border-blue-500/20 bg-blue-500/[0.03] px-4 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-assign-bar">
      <div className="flex items-center gap-3 text-xs">
        <CheckSquare className="h-4 w-4 text-blue-400" />
        {count > 0 ? (
          <span className="text-zinc-200"><span className="font-semibold text-blue-300">{count}</span> selected</span>
        ) : (
          <span className="text-zinc-400">{unassignedInView} unassigned in view</span>
        )}
        {count === 0 && unassignedInView > 0 && (
          <button onClick={onSelectAll} className="text-[11px] text-blue-300 hover:text-blue-200 underline" data-testid="bulk-select-unassigned">
            Select all unassigned
          </button>
        )}
        {count > 0 && (
          <button onClick={onClear} className="text-[11px] text-zinc-400 hover:text-zinc-200 underline" data-testid="bulk-clear">
            Clear
          </button>
        )}
      </div>
      {count > 0 && (
        <div className="flex items-center gap-2">
          <Select data-testid="customers-select-746" value={picked} onValueChange={setPicked}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-56" data-testid="bulk-assign-select"><SelectValue placeholder="Pick a Member Manager" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {coaches.length === 0 ? (
                <SelectItem data-testid="customers-select-item-750" disabled value="_none">No member managers on roster</SelectItem>
              ) : (coaches || []).map((c) => (<SelectItem data-testid="customers-select-item-751" key={c.id} value={c.name}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={busy || !picked} onClick={assign} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="bulk-assign-confirm">
            {busy ? "Assigning…" : `Assign to ${count}`}
          </Button>
        </div>
      )}
    </div>
  );
}
